import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Calendar, MapPin, Globe, ExternalLink, Users, Ticket, Megaphone, IdCard, ScanLine, BarChart3, LayoutTemplate, Smartphone, Settings2, Crown } from "lucide-react";
import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { formatEventDateRange } from "@/lib/event-dates";
import { toast } from "sonner";
import { matchSponsorAttendees } from "@/lib/ai-segments-api";
import GovernmentIntegrationPanel from "@/components/GovernmentIntegrationPanel";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

export default function EventDetail() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sponsorForm, setSponsorForm] = useState({ name: "", tier: "" });
  const [sponsorBusy, setSponsorBusy] = useState(false);
  const [sponsorMatches, setSponsorMatches] = useState({});
  const [matchBusyId, setMatchBusyId] = useState("");
  const [exhibitorForm, setExhibitorForm] = useState({ name: "", booth: "" });
  const [exhibitorBusy, setExhibitorBusy] = useState(false);

  const reload = async () => {
    const res = await fetch(`${API_BASE}/api/admin/events/${id}/overview`);
    if (!res.ok) throw new Error(`Failed to load event (${res.status})`);
    return res.json();
  };

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError("");
    setPayload(null);
    (async () => {
      try {
        const d = await reload();
        if (!cancel) {
          setPayload(d);
          setError("");
        }
      } catch (err) {
        if (!cancel) setError(err?.message || "Failed to load event");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  const e = payload?.event;
  const sessions = payload?.sessions || [];
  const tierSales = payload?.tierSales || [];
  const insights = payload?.insights || {};
  const sponsors = Array.isArray(payload?.sponsors) ? payload.sponsors : [];
  const exhibitors = Array.isArray(payload?.exhibitors) ? payload.exhibitors : [];
  const registered = e?.registered || 0;
  const checkedInLive = e?.checkedIn;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Always prefer the EMS microsite. `event.website` is sometimes set to an
  // external listing used as source material — that must not replace /e/<slug>.
  const emsPublicPath = `${origin}/e/${e?.slug || id}`;
  const rawWebsite = (e?.website || "").trim();
  const publicWebsite =
    rawWebsite && /aivoryems\.com\/e\/|\/e\/[a-z0-9-]+/i.test(rawWebsite)
      ? rawWebsite
      : emsPublicPath;

  if (loading) {
    return <div className="p-8 lg:p-12 text-sm text-[var(--text-secondary)]">Loading event...</div>;
  }

  if (!e) {
    return <div className="p-8 lg:p-12 text-sm text-[var(--error)]">{error || "Event not found."}</div>;
  }

  return (
    <div data-testid="event-detail">
      {e?.isSandboxDemo ? (
        <div className="mx-4 md:mx-8 lg:mx-12 mt-4 md:mt-6 surface p-4 border border-[#A78BFA]/40 bg-[#A78BFA]/10">
          <div className="text-overline text-[#C4B5FD]">Sandbox demo</div>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            This event is for exploration only. Edits here do not affect workspace KPIs, billing meters, or plan limits.
            Public registration is disabled while the event stays in Draft.
          </p>
        </div>
      ) : null}
      {/* Hero */}
      <div className="relative border-b border-[var(--border-default)] overflow-hidden">
        <div className="absolute inset-0">
          {e?.cover ? (
            <img src={e.cover} alt="" className="w-full h-full object-cover opacity-25" />
          ) : (
            <div className="w-full h-full bg-[var(--bg-elev)] opacity-40" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base-alpha80)] to-transparent" />
        </div>
        <div className="relative p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto">
          <div className="text-overline truncate">/ events / {e?.title || e?.slug || id}</div>
          <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mt-4">
            <div className="min-w-0">
              <span
                className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border text-brand"
                style={{ borderColor: "color-mix(in srgb, var(--primary) 33%, transparent)" }}
              >
                {e?.status}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl tracking-tight font-medium mt-4 break-words">{e?.title || "Loading event..."}</h1>
              <p className="text-[var(--text-secondary)] mt-2">{e?.subtitle || ""}</p>
              <div className="flex flex-wrap gap-6 mt-5 text-sm text-[var(--text-secondary)]">
                <span className="flex items-center gap-2"><Calendar size={14} /> {formatEventDateRange(e?.startDate, e?.endDate)}</span>
                <span className="flex items-center gap-2"><MapPin size={14} /> {e?.venue}</span>
                {e?.format ? <span className="flex items-center gap-2 font-mono">{e.format}</span> : null}
                <span className="flex items-center gap-2 font-mono"><Globe size={14} /> {publicWebsite.replace(/^https?:\/\//, "")}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <a href={publicWebsite} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="view-public-page"><ExternalLink size={14} /> Public page</a>
              <Link to={`/admin/events/${e?.id || id}/checkin`} className="btn-primary" data-testid="open-live-ops"><ScanLine size={14} /> Live ops</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {[
            ["Registered", registered.toLocaleString()],
            ["Capacity", (e?.capacity || 0).toLocaleString()],
            [
              "Checked in",
              checkedInLive != null ? checkedInLive.toLocaleString() : "—",
            ],
            [
              "Check-in rate",
              registered > 0 && checkedInLive != null
                ? `${Math.round((checkedInLive / registered) * 100)}%`
                : "—",
            ],
            [
              "Tickets sold",
              insights.ticketsSold != null ? `${insights.ticketsSold}/${insights.ticketsQuota || "—"}` : "—",
            ],
          ].map(([k, v]) => (
            <div key={k} className="bg-[var(--bg-surface)] p-4 md:p-6">
              <div className="text-overline">{k}</div>
              <div className="font-mono text-2xl md:text-3xl mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Modules grid */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-12 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          <ModuleCard span={4} icon={Users} to={`/admin/events/${e?.id || id}/attendees`} title="Attendees" body={`${registered.toLocaleString()} registered`} />
          <ModuleCard span={4} icon={Ticket} to={`/admin/events/${e?.id || id}/tickets`} title="Tickets" body={`${tierSales.length} tiers configured`} />
          <ModuleCard span={4} icon={Megaphone} to={`/admin/events/${e?.id || id}/communications`} title="Communications" body="Campaigns and delivery metrics" />
          <ModuleCard span={4} icon={LayoutTemplate} to={`/admin/events/${e?.id || id}/landing`} title="Page Builder" body="Manage event landing content" featured />
          <ModuleCard span={4} icon={Settings2} to={`/admin/events/${e?.id || id}/basics`} title="Event basics" body="Location, date, website, slug" />
          <ModuleCard span={4} icon={IdCard} to={`/admin/events/${e?.id || id}/badge`} title="Badge designer" body="Template and QR settings" />
          <ModuleCard span={4} icon={Smartphone} to={`/admin/events/${e?.id || id}/wallet`} title="Wallet pass" body="Distribution and pass templates" />
          <ModuleCard span={4} icon={ScanLine} to={`/admin/events/${e?.id || id}/checkin`} title="Live check-in" body="Onsite operations console" featured />
          <ModuleCard span={4} icon={BarChart3} to={`/admin/events/${e?.id || id}/reports`} title="Reports" body="Exportable analytics datasets" />
        </div>

        {/* Sessions teaser */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-1 md:col-span-8 surface p-4 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-overline">Schedule</div>
                <h3 className="font-heading text-2xl mt-1">Sessions</h3>
              </div>
              <Link to={`/admin/events/${e?.id || id}/sessions`} className="btn-ghost text-sm">Manage →</Link>
            </div>
            <div className="mt-6 divide-y divide-[var(--border-default)]">
              {sessions.map((s) => {
                const cap = Math.max(1, Number(s.capacity || 0));
                const reg = Number(s.registered || 0);
                const pct = Math.min(100, (reg / cap) * 100);
                return (
                  <div key={s.id} className="py-4 grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-7">
                      <div className="font-medium text-sm">{s.title}</div>
                      <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">{s.time} · {s.room}</div>
                    </div>
                    <div className="col-span-3 text-xs font-mono text-right">{reg}/{s.capacity || 0}</div>
                    <div className="col-span-2 h-1 bg-[var(--bg-elev)]">
                      <div className="h-full" style={{ width: `${pct}%`, background: pct >= 99 ? "var(--error)" : "var(--primary)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {!sessions.length && <div className="mt-6 text-sm text-[var(--text-secondary)]">No sessions available.</div>}
          </div>

          <div className="col-span-1 md:col-span-4 surface p-4 md:p-8">
            <div className="text-overline">Registration by tier</div>
            <div className="mt-6 space-y-4">
              {tierSales.map(([n, s, q, c]) => (
                <div key={n}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{n}</span>
                    <span className="font-mono">{s}/{q}</span>
                  </div>
                  <div className="mt-2 h-1 bg-[var(--bg-elev)]"><div className="h-full bg-brand" style={{ width: `${(s / q) * 100}%` }} /></div>
                </div>
              ))}
            </div>
            {!tierSales.length && <div className="mt-6 text-sm text-[var(--text-secondary)]">No tier sales data available.</div>}
            {insights.sessionCount != null ? (
              <div className="mt-6 text-xs font-mono text-[var(--text-tertiary)]">
                {insights.sessionCount} sessions · {insights.sponsorCount || 0} sponsors · {insights.exhibitorCount || 0} exhibitors
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-12 surface p-4 md:p-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-overline">Partners</div>
              <h3 className="font-heading text-2xl mt-1">Sponsors</h3>
            </div>
            <Link to={`/admin/events/${e?.id || id}/landing`} className="btn-ghost text-sm">Show on landing →</Link>
          </div>
          <form
            className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2"
            onSubmit={async (ev) => {
              ev.preventDefault();
              if (!sponsorForm.name.trim()) return;
              setSponsorBusy(true);
              try {
                const res = await fetch(`${API_BASE}/api/events/${e?.id || id}/sponsors`, {
                  method: "POST",
                  headers: { "content-type": "application/json", ...getAuthHeaders() },
                  body: JSON.stringify({ name: sponsorForm.name.trim(), tier: sponsorForm.tier.trim() }),
                });
                if (!res.ok) throw new Error("Save failed");
                setSponsorForm({ name: "", tier: "" });
                setPayload(await reload());
                toast.success("Sponsor saved");
              } catch (err) {
                toast.error(err?.message || "Could not save sponsor");
              } finally {
                setSponsorBusy(false);
              }
            }}
          >
            <input className="input-dark" placeholder="Sponsor name" value={sponsorForm.name} onChange={(ev) => setSponsorForm((s) => ({ ...s, name: ev.target.value }))} />
            <input className="input-dark" placeholder="Package tier" value={sponsorForm.tier} onChange={(ev) => setSponsorForm((s) => ({ ...s, tier: ev.target.value }))} />
            <button type="submit" className="btn-primary" disabled={sponsorBusy}>Add</button>
          </form>
          <div className="mt-4 divide-y divide-[var(--border-default)]">
            {sponsors.map((sp) => (
              <div key={sp.id || sp.name} className="py-3 border-b border-[var(--border-default)]">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Crown size={14} className="text-[var(--primary)] shrink-0" />
                    <span className="font-medium truncate">{sp.name}</span>
                    {sp.tier ? <span className="text-xs font-mono text-[var(--text-tertiary)]">{sp.tier}</span> : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {sp.id ? (
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        disabled={matchBusyId === sp.id}
                        onClick={async () => {
                          setMatchBusyId(sp.id);
                          try {
                            const body = await matchSponsorAttendees({
                              eventId: e?.id || id,
                              sponsorName: sp.name,
                              sponsorGoals: sp.tier || "",
                            });
                            setSponsorMatches((prev) => ({ ...prev, [sp.id]: body.matches || [] }));
                            toast.success("Attendee matches ready", { description: `${(body.matches || []).length} suggestions` });
                          } catch (err) {
                            toast.error(err?.message || "Could not match attendees");
                          } finally {
                            setMatchBusyId("");
                          }
                        }}
                      >
                        {matchBusyId === sp.id ? "Matching…" : "Match"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-xs text-[var(--error)]"
                      onClick={async () => {
                        if (!sp.id) return;
                        try {
                          const res = await fetch(`${API_BASE}/api/events/${e?.id || id}/sponsors/${sp.id}`, {
                            method: "DELETE",
                            headers: getAuthHeaders(),
                          });
                          if (!res.ok) throw new Error("Delete failed");
                          setPayload(await reload());
                        } catch (err) {
                          toast.error(err?.message || "Could not delete sponsor");
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {sp.id && Array.isArray(sponsorMatches[sp.id]) && sponsorMatches[sp.id].length ? (
                  <div className="mt-2 text-[10px] font-mono text-[var(--text-tertiary)] pl-6">
                    {(sponsorMatches[sp.id] || []).slice(0, 4).map((m) => m.title || m.subtitle).filter(Boolean).join(" · ")}
                  </div>
                ) : null}
              </div>
            ))}
            {!sponsors.length ? <div className="py-4 text-sm text-[var(--text-secondary)]">No sponsors yet.</div> : null}
          </div>
        </div>

        <div className="mt-8 surface p-4 md:p-8" data-testid="event-gov-integration">
          <GovernmentIntegrationPanel eventId={e?.id || id} />
        </div>

        <div className="mt-8 surface p-4 md:p-8">
          <div className="text-overline">Partners</div>
          <h3 className="font-heading text-2xl mt-1">Exhibitors</h3>
          <form
            className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2"
            onSubmit={async (ev) => {
              ev.preventDefault();
              if (!exhibitorForm.name.trim()) return;
              setExhibitorBusy(true);
              try {
                const res = await fetch(`${API_BASE}/api/events/${e?.id || id}/exhibitors`, {
                  method: "POST",
                  headers: { "content-type": "application/json", ...getAuthHeaders() },
                  body: JSON.stringify({ name: exhibitorForm.name.trim(), booth: exhibitorForm.booth.trim() }),
                });
                if (!res.ok) throw new Error("Save failed");
                setExhibitorForm({ name: "", booth: "" });
                setPayload(await reload());
                toast.success("Exhibitor saved");
              } catch (err) {
                toast.error(err?.message || "Could not save exhibitor");
              } finally {
                setExhibitorBusy(false);
              }
            }}
          >
            <input className="input-dark" placeholder="Exhibitor name" value={exhibitorForm.name} onChange={(ev) => setExhibitorForm((s) => ({ ...s, name: ev.target.value }))} />
            <input className="input-dark" placeholder="Booth / hall" value={exhibitorForm.booth} onChange={(ev) => setExhibitorForm((s) => ({ ...s, booth: ev.target.value }))} />
            <button type="submit" className="btn-primary" disabled={exhibitorBusy}>Add</button>
          </form>
          <div className="mt-4 divide-y divide-[var(--border-default)]">
            {exhibitors.map((ex) => (
              <div key={ex.id || ex.name} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{ex.name}</div>
                  {ex.booth ? <div className="text-xs font-mono text-[var(--text-tertiary)]">{ex.booth}</div> : null}
                </div>
                <button
                  type="button"
                  className="text-xs text-[var(--error)]"
                  onClick={async () => {
                    if (!ex.id) return;
                    try {
                      const res = await fetch(`${API_BASE}/api/events/${e?.id || id}/exhibitors/${ex.id}`, {
                        method: "DELETE",
                        headers: getAuthHeaders(),
                      });
                      if (!res.ok) throw new Error("Delete failed");
                      setPayload(await reload());
                    } catch (err) {
                      toast.error(err?.message || "Could not delete exhibitor");
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            {!exhibitors.length ? <div className="py-4 text-sm text-[var(--text-secondary)]">No exhibitors yet.</div> : null}
          </div>
        </div>
        {error && !loading && <div className="mt-6 text-sm text-[var(--error)]">{error}</div>}
      </div>
    </div>
  );
}

function ModuleCard({ span, icon: Icon, to, title, body, featured }) {
  return (
    <Link
      to={to}
      data-testid={`module-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-[var(--bg-surface)] surface-hover p-5 md:p-7 group flex flex-col justify-between min-h-[120px] md:min-h-[140px] col-span-1 md:col-span-4 ${featured ? "border" : ""}`}
      style={featured ? { borderColor: "color-mix(in srgb, var(--primary) 40%, transparent)" } : undefined}
    >
      <Icon size={20} strokeWidth={1.5} className={featured ? "text-brand" : "text-[var(--text-primary)]"} />
      <div>
        <div className="font-heading text-lg mt-4">{title}</div>
        <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1 flex items-center justify-between">
          <span>{body}</span>
          <ArrowRight size={14} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-hover:translate-x-1 transition" />
        </div>
      </div>
    </Link>
  );
}

